import FormBuilder from 'components/formBuilder';
import { pyAPI } from 'constants/api';
import { appColors, imageURLs, pageURL_PY } from 'constants/index';
import {
  ACTIVATION,
  CLIENT_DETAILS_LOADED,
  CLIENT_DETAILS_SUBMITTED,
  MESSAGE_DISPLAYED,
  SERVER_RESPONSE_FAILED,
} from 'constants/segmentEvents';
import useMutationWrapper from 'hooks/queries/useMutationWrapper';
import useQueryWrapper from 'hooks/queries/useQueryWrapper';
import useFormBuilder from 'hooks/useFormBuilder';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Label, Layout } from 'tigo-components';
import {
  generateMessageDisplayedSegment,
  generateServerResponseError,
  globalTrackValues,
  track,
  updateGlobalTracker,
} from 'utils/segmenTracker';
import { generateIntialFormData, removeWhiteSpace } from 'utils/utils';
import { LabelWrapper, Wrapper } from './styled';
import { formatFormFields, formValidator, getCreateLeadParams, getDocUploadParams } from './utils';
import moment from 'moment';

const ProspectInformation = ({ formData: { title, formFields, redirectURLs }, history }) => {

  const activationState = useSelector((state) => state.activation);
  const locationState = useSelector((state) => state.location);
  const [isProcessing, setIsProcessing] = useState(false);
  const [geo, setGeo] = useState([]);
  const { clientDetails, location } = activationState;

  const {
    activation: { updateClientInformation, setDisabledStatus },
  } = useDispatch();

  const {
    formData: initialData,
    updateFormData,
    updateErrorData,
    resetFormData,
  } = useFormBuilder(generateIntialFormData(formFields, clientDetails));

  const [userId, setUserId] = useState('');
  const [errorMsg, setError] = useState('');

  const dom = useLocation();

  const {
    state: { prospect: prospectEdit },
  } = dom?.state || { state: {} };

  const { isFetching, refetch } = useQueryWrapper(
    ['id', initialData?.data?.documentNumber],
    pyAPI.getClientInfoFromDB.replace(':id', initialData?.data?.documentNumber),
    null,
    {
      success: (response) => {
        setDisabledStatus(true);
        updateErrorData({ id: 'cliFirstName', value: false });
        updateErrorData({ id: 'cliLastName', value: false });
        updateFormData({
          cliFirstName: response?.name,
          cliLastName: response?.lastName,
          dateOfBirth: response?.birthDate,
          country: response?.nationality,
        });
      },
      error: (err) => {
        setDisabledStatus(false);
        updateFormData({ cliFirstName: '', cliLastName: '' });
        updateErrorData({ id: 'documentNumber', value: true });
        const message = err?.rawResponse?.error?.message;
        track(SERVER_RESPONSE_FAILED, {
          ...generateServerResponseError(
            err,
            ACTIVATION,
            globalTrackValues.PY.page_name,
            pyAPI.getClientInfoFromDB.replace(':id', initialData?.data?.documentNumber),
            message
          ),
        });
        track(MESSAGE_DISPLAYED, {
          ...generateMessageDisplayedSegment(err, ACTIVATION, 'Get client information from db', message),
        });
        setError(err?.rawResponse?.error?.message);
      },
    }
  );

  const { isFetching: isFetchingClientInfo, refetch: refetchClientInfo } = useQueryWrapper(
    ['ruc', initialData?.data?.documentNumber],
    pyAPI.getCustomerInfo.concat(`?searchType=CUSTOMER_ACCOUNT&searchValue=${initialData?.data?.documentNumber}RUPY`),
    null,
    {
      success: (response) => {
        setDisabledStatus(true);
        updateErrorData({ id: 'cliFirstName', value: false });
        updateErrorData({ id: 'cliLastName', value: false });
        updateFormData({
          cliFirstName: response?.customer?.name,
          cliLastName: response?.lastName || '-',
          dateOfBirth: response?.customer?.contact[0]?.characteristics?.characteristics?.find(
            (i) => i.name === 'BIRTH_DAY'
          )?.value,
          country: 'PY',
        });
      },
      error: (err) => {
        setDisabledStatus(false);
        updateFormData({ cliFirstName: '', cliLastName: '' });
        updateErrorData({ id: 'documentNumber', value: true });
        const message = err?.rawResponse?.error?.message;
        track(SERVER_RESPONSE_FAILED, {
          ...generateServerResponseError(
            err,
            ACTIVATION,
            globalTrackValues.PY.page_name,
            pyAPI.getCustomerInfo.concat(
              `?searchType=CUSTOMER_ACCOUNT&searchValue=${initialData?.data?.documentNumber}RUPY`
            ),
            message
          ),
        });
        track(MESSAGE_DISPLAYED, {
          ...generateMessageDisplayedSegment(err, ACTIVATION, 'Get client information from db', message),
        });
        setError(
          'El documento que tratas de activar no cumple con las condiciones comerciales, favor consultar con un asesor para validar los datos ingresados y volvé a intentarlo! Gracias'
        );
      },
    }
  );

  const { mutate, isLoading } = useMutationWrapper(pyAPI.createProspect, {
    successCallback: (response) => {
      setIsProcessing(false);
      history.push(redirectURLs.nextURL);
    },
    errorCallback: (err) => {
      const message = err?.rawResponse?.error?.message || err?.rawResponse?.errors?.message;
      track(SERVER_RESPONSE_FAILED, {
        ...generateServerResponseError(err, ACTIVATION, globalTrackValues.PY.page_name, pyAPI.createLead, message),
      });
      track(MESSAGE_DISPLAYED, {
        ...generateMessageDisplayedSegment(err, ACTIVATION, 'Create Lead API', message),
      });
      setIsProcessing(false);
      setError(err?.rawResponse?.error?.message || err?.rawResponse?.errors?.message);
    },
  });

  const { mutate: patchProspectMutation, isPatching } = useMutationWrapper(
    pyAPI.prospect.replace(':prospectId', prospectEdit?.id),
    {
      successCallback: (response) => {
        setIsProcessing(false);
        history.push(redirectURLs.nextURL);
      },
      errorCallback: (err) => {
        const message = err?.rawResponse?.error?.message || err?.rawResponse?.errors?.message;
        track(SERVER_RESPONSE_FAILED, {
          ...generateServerResponseError(err, ACTIVATION, globalTrackValues.PY.page_name, pyAPI.createLead, message),
        });
        track(MESSAGE_DISPLAYED, {
          ...generateMessageDisplayedSegment(err, ACTIVATION, 'Create Lead API', message),
        });
        setError(err?.rawResponse?.error?.message || err?.rawResponse?.errors?.message);
        setIsProcessing(false);
      },
    },
    {},
    'PATCH'
  );

  const { mutate: updateDocument, isLoading: isLoadingDoc } = useMutationWrapper(
    ['dth', 'hfc'].includes(clientDetails?.selectedButtonText)
      ? pyAPI.updateLeadLocation.replace(':id', clientDetails?.basketId)
      : pyAPI.updateLeadLocationV1.replace(':id', clientDetails?.basketId),
    {
      successCallback: (response) => {
        history.push(redirectURLs.nextURL);
      },
      errorCallback: (err) => {
        const message = err?.rawResponse?.error?.message || err?.rawResponse?.errors?.message;
        track(SERVER_RESPONSE_FAILED, {
          ...generateServerResponseError(
            err,
            ACTIVATION,
            globalTrackValues.PY.page_name,
            ['dth', 'hfc'].includes(clientDetails?.selectedButtonText)
              ? pyAPI.updateLeadLocation.replace(':id', clientDetails?.basketId)
              : pyAPI.updateLeadLocationV1.replace(':id', clientDetails?.basketId),
            message
          ),
        });
        track(MESSAGE_DISPLAYED, {
          ...generateMessageDisplayedSegment(err, ACTIVATION, 'Upload Document API', message),
        });
        setError(err?.rawResponse?.error?.message || err?.rawResponse?.errors?.message);
      },
    },
    {},
    'PATCH'
  );

  const { isFetching: isValidatingClient, refetch: refetchValidateClient } = useQueryWrapper(
    ['basketID', userId],
    pyAPI.validateClient.replace(':basketID', userId),
    null,
    {
      success: (response) => {
        const validationInfo = response?.data?.eligibilityDecisions?.decisionContent?.filter(
          (info) => info?.status !== 'OK'
        );
        updateClientInformation({
          validationInfo,
        });
        if (validationInfo.length) history.push(redirectURLs.redirectURL);
        else {
          updateDocument({ ...getDocUploadParams(clientDetails) });
        }
      },
      error: (err) => {
        setUserId('');
        const validationInfo = err?.rawResponse?.data?.eligibilityDecisions?.decisionContent?.filter(
          (info) => info?.status !== 'OK'
        );
        const message = err?.rawResponse?.error?.errors?.[0]?.message || err?.rawResponse?.error?.message;
        if (!validationInfo) {
          setError(err?.rawResponse?.error?.errors?.[0]?.message || err?.rawResponse?.error?.message || err?.message);
          return;
        }
        updateClientInformation({
          validationInfo,
        });

        track(SERVER_RESPONSE_FAILED, {
          ...generateServerResponseError(
            err,
            ACTIVATION,
            globalTrackValues.PY.page_name,
            pyAPI.validateClient.replace(':basketID', userId),
            message
          ),
        });
        if (validationInfo.length) {
          history.push(redirectURLs.redirectURL);
        } else {
          setError(message);
          track(MESSAGE_DISPLAYED, {
            ...generateMessageDisplayedSegment(err, ACTIVATION, 'Validate client', message),
          });
        }
      },
    }
  );

  useEffect(() => {
    track(CLIENT_DETAILS_LOADED, { journey: ACTIVATION });
    updateGlobalTracker({ ...globalTrackValues.PY });
  }, []);

  const ref = useRef(location);

  useEffect(() => {
    if (ref) {
      updateFormData({
        location: `${ref.current?.[0]}, ${ref.current?.[1]}`,
      });
    }
  }, [ref]);

  useEffect(() => {
    if (prospectEdit) {
      updateFormData({
        documentNumber: prospectEdit?.document_number || '',
        documentType: prospectEdit?.document_type || 'CI',
        cliEmail: prospectEdit?.client_email || '',
        dateOfBirth: prospectEdit?.client_dob || '',
        // reminderDate: prospectEdit?.reminder_date || '',
        // !p?.reminder_date?.split('T')?.[0].startsWith('1899') ? moment(p?.reminder_date?.split('T')?.[0])?.format('DD/MM/YYYY') : '',
        reminderDate: prospectEdit?.reminder_date?.split('T')?.[0] ? prospectEdit?.reminder_date?.split('T')?.[0] : '',
        cliTelephone: prospectEdit?.client_phone || '',
        cliFirstName: prospectEdit?.client_firstname || '',
        cliLastName: prospectEdit?.client_lastname || '',
        salesType: prospectEdit?.sales_type || '',
        addressType: prospectEdit?.address_type || '',
        addressRef: prospectEdit?.address_ref || '',
        location: `${prospectEdit?.client_latitude}, ${prospectEdit?.client_longitude}`,
      });
      prospectEdit?.document_number && refetch();
    }
  }, [prospectEdit]);

  useEffect(() => {
    if (!clientDetails?.isLoggedIn) {
      history.push(pageURL_PY.home);
    }
  }, [clientDetails, history]);

  useEffect(() => {
    if (userId) refetchValidateClient();
  }, [userId, refetchValidateClient]);


  useEffect(() => {

    const getLocation = async () => {
      const geo = await getCurrentLocation();
      setGeo(geo);
    }

    getLocation();

  }, [])


  const handleSubmit = async (data) => {
    //
    const dateOfBirth = moment(data.dateOfBirth).format('YYYY-MM-DD');
    const reminderDate = moment(data.reminderDate).format('YYYY-MM-DD');

    data.dateOfBirth = moment(data.dateOfBirth).format('YYYY-MM-DD');
    data.reminderDate = moment(data.reminderDate).format('YYYY-MM-DD');
    data.salesman_latitude = geo[0].toString();
    data.salesman_longitude = geo[1].toString();

    console.log('Submitted Data: ', data);

    const isError = formValidator(data, updateErrorData, setError);

    if (!isError && !isProcessing) {

      setIsProcessing(true);

      const payload = {
        salesman_latitude: geo[0].toString(),
        salesman_longitude: geo[1].toString(),
        document_type: data.documentType,
        document_number: removeWhiteSpace(data.documentNumber),
        client_firstname: data.cliFirstName,
        client_lastname: data.cliLastName,
        client_email: data.cliEmail || '-',
        client_phone: data.cliTelephone || '-',
        client_nationality: data.cliNationality || '-',
        client_latitude: data.location?.split(',')[0].trim(),
        client_longitude: data.location?.split(',')[1].trim(),
        client_dob: dateOfBirth,
        reminder_date: reminderDate,
        address_type: data.addressType || '-',
        address_ref: data.addressRef || '-',
        sales_type: data.salesType || '-',
        interested_in: data.interestedIn,
      };

      if (prospectEdit) {
        patchProspectMutation({ ...payload });
      } else {
        mutate({
          ...payload,
          state_activation: JSON.stringify(activationState),
          state_location: JSON.stringify(locationState),
        });
      }
    }
  };

  function getCurrentLocation(callback) {
    return new Promise((resolve, _reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            resolve([latitude, longitude]);
          },
          (error) => {
            console.error('Error getting location:', error);
            resolve(['-25.30066', '-57.63591']);
          }
        );
      } else {
        console.error('Geolocation is not supported by this browser.');
        resolve(['-25.30066', '-57.63591']);
      }
    });
  }

  const handleBack = () => history.goBack();

  return (
    <Layout
      showHeader
      logo={imageURLs.logo}
      bgColor={appColors.landingBgWhite}
      loading={isFetching || isValidatingClient || isLoading || isLoadingDoc || isFetchingClientInfo || isPatching}
      error={errorMsg ? { title: '', description: errorMsg, type: 'locationError' } : null}
      fullWidth={true}
      headerShadow
      showBack={true}
      backButtonImage={imageURLs.backButton}
      onBackButtonClick={handleBack}
      maxWidth={'600px'}
      clearError={() => setError('')}
    >
      <Wrapper error={initialData?.errors?.cliNationality}>
        <LabelWrapper>
          <Label text={title} fontSize={'x-large'} />
        </LabelWrapper>
        <FormBuilder
          formFields={formatFormFields(
            formFields,
            initialData?.data,
            clientDetails.disableNameFields,
            clientDetails.stepFromSalesReport ? true : false
          )}
          handleSubmit={handleSubmit}
          formData={initialData}
          updateFormData={(data) => {
            let dt = { ...data };
            if (Object.hasOwn(dt, 'cliTelephone')) {
              dt = { ...dt, cliTelephone: data.cliTelephone?.replace(/[^0-9.]/g, '') };
            }
            updateFormData(dt);
          }}
          updateErrorData={updateErrorData}
          handleCancel={() => history.push(pageURL_PY.prospects)}
          customInputProps={{
            documentNumber: {
              onBlur: (e) => {
                if (e.target.value && e.target.id === 'documentNumber' && initialData?.data?.documentType === 'CI')
                  refetch();

                if (e.target.value && e.target.id === 'documentNumber' && initialData?.data?.documentType === 'RUC')
                  refetchClientInfo();
              },
            },
            documentType: {
              onClick: () => {
                resetFormData();
                setDisabledStatus(false);
              },
            },
          }}
          multiCalendar={true}
          validateOnChange={false}
        />
      </Wrapper>
    </Layout>
  );
};

ProspectInformation.defaultProps = {
  title: '',
  formFields: [],
};

ProspectInformation.propTypes = {
  title: PropTypes.string,
  formFields: PropTypes.array.isRequired,
};

export default ProspectInformation;
